// src/components/shared/ui/TabBar.tsx
import React from "react";
import { Link } from "react-router-dom";

export type TabItem = {
  key: string;
  label: string;
  /** Optional badge, e.g. how many records sit behind the tab. */
  count?: number;
  /** Set for route-backed tabs; omit for tabs that only change local state. */
  to?: string;
};

type Props = {
  items: readonly TabItem[];
  activeKey: string;
  /** Called for tabs without a `to`. */
  onSelect?: (key: string) => void;
  ariaLabel: string;
  className?: string;
};

const tabClass = (active: boolean) =>
  `-mb-px inline-flex items-center gap-2 border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
    active
      ? "border-sky-600 text-sky-700"
      : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
  }`;

const Count: React.FC<{ value: number; active: boolean }> = ({ value, active }) => (
  <span
    className={`inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full px-1.5 text-[11px] font-semibold ${
      active ? "bg-sky-100 text-sky-700" : "bg-gray-100 text-gray-600"
    }`}
  >
    {value}
  </span>
);

/**
 * Underline tab bar — one look for every tabbed screen, whether the tabs are
 * routes (each tab has its own URL) or local state.
 */
const TabBar: React.FC<Props> = ({
  items,
  activeKey,
  onSelect,
  ariaLabel,
  className = "",
}) => (
  <div
    role="tablist"
    aria-label={ariaLabel}
    className={`flex flex-wrap items-center gap-1 border-b border-gray-200 ${className}`}
  >
    {items.map((item) => {
      const active = item.key === activeKey;

      const content = (
        <>
          <span>{item.label}</span>
          {typeof item.count === "number" && (
            <Count value={item.count} active={active} />
          )}
        </>
      );

      return item.to ? (
        <Link
          key={item.key}
          to={item.to}
          role="tab"
          aria-selected={active}
          className={tabClass(active)}
        >
          {content}
        </Link>
      ) : (
        <button
          key={item.key}
          type="button"
          role="tab"
          aria-selected={active}
          onClick={() => onSelect?.(item.key)}
          className={tabClass(active)}
        >
          {content}
        </button>
      );
    })}
  </div>
);

export default TabBar;
